<script setup lang="ts">
const { isLoggedIn } = useAuth()

onMounted(() => {
  if (!isLoggedIn.value) navigateTo('/account/login')
})

const { data: rawOrders, pending } = useFetch('/api/customer/orders', { server: false })
const allOrders = computed(() => (rawOrders.value as any[]) ?? [])

const search = ref('')
const activeFilter = ref('All')
const currentPage = ref(1)
const perPage = 6
const filters = ['All', 'Processing', 'Delivered', 'Refunded']

const filteredOrders = computed(() => {
  return allOrders.value.filter((o: any) => {
    const matchesFilter = activeFilter.value === 'All' || o.status === activeFilter.value
    const matchesSearch = !search.value || o.name.toLowerCase().includes(search.value.toLowerCase())
    return matchesFilter && matchesSearch
  })
})

const totalPages = computed(() => Math.ceil(filteredOrders.value.length / perPage))

const pagedOrders = computed(() => {
  const start = (currentPage.value - 1) * perPage
  return filteredOrders.value.slice(start, start + perPage)
})

const showingText = computed(() => {
  const start = (currentPage.value - 1) * perPage + 1
  const end = Math.min(currentPage.value * perPage, filteredOrders.value.length)
  return `Showing ${start}–${end} of ${filteredOrders.value.length} order${filteredOrders.value.length !== 1 ? 's' : ''}`
})

function setFilter(f: string) {
  activeFilter.value = f
  currentPage.value = 1
}

function toBase64url(str: string): string {
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function orderDetailLink(order: any) {
  return `/account/orders/${toBase64url(order.id)}`
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function statusBg(status: string) {
  return status === 'Processing' ? 'bg-surface' : 'bg-raised'
}
</script>

<template>
  <div class="bg-page flex flex-col items-start">
    <!-- Page Header -->
    <div class="flex flex-col gap-2 items-start pb-8 pt-14 px-4 md:px-20 w-full">
      <p class="font-sans text-[13px] text-muted">Account &nbsp;/&nbsp; Order History</p>
      <p class="font-serif font-semibold text-4xl md:text-[42px] text-dark">Order History</p>
      <p class="font-sans text-[15px] text-secondary">
        <span v-if="pending">Loading…</span>
        <span v-else>{{ filteredOrders.length }} order{{ filteredOrders.length !== 1 ? 's' : '' }}</span>
      </p>
    </div>

    <!-- Filters -->
    <div class="flex flex-wrap gap-3 items-center pb-6 px-4 md:px-20 w-full">
      <input
        v-model="search"
        type="text"
        placeholder="Search orders…"
        class="bg-surface border border-stroke font-sans text-sm text-dark placeholder:text-muted h-11 px-3.5 rounded-lg outline-none focus:border-brand transition-colors w-full md:w-[280px]"
      />
      <button
        v-for="f in filters"
        :key="f"
        class="flex items-center px-4 py-2.5 rounded-full font-sans font-medium text-[13px] transition-colors"
        :class="activeFilter === f ? 'bg-brand text-page' : 'border border-stroke text-secondary hover:border-brand'"
        @click="setFilter(f)"
      >
        {{ f }}
      </button>
    </div>

    <!-- Table -->
    <div class="flex flex-col items-start pb-12 px-4 md:px-20 w-full">
      <div class="bg-surface flex flex-col items-start rounded-xl w-full overflow-x-auto">
        <!-- Table Head -->
        <div class="bg-raised flex h-12 items-center px-6 w-full min-w-[800px]">
          <div class="flex h-12 items-center w-[280px] md:w-[320px]">
            <p class="font-sans font-semibold text-xs text-muted">Order</p>
          </div>
          <div class="flex h-12 items-center w-[160px] md:w-[200px]">
            <p class="font-sans font-semibold text-xs text-muted">Date</p>
          </div>
          <div class="flex h-12 items-center w-[100px] md:w-[200px]">
            <p class="font-sans font-semibold text-xs text-muted">Items</p>
          </div>
          <div class="flex h-12 items-center w-[100px] md:w-[200px]">
            <p class="font-sans font-semibold text-xs text-muted">Total</p>
          </div>
          <div class="flex h-12 items-center w-[120px] md:w-[200px]">
            <p class="font-sans font-semibold text-xs text-muted">Status</p>
          </div>
          <div class="flex-1 h-12" />
        </div>

        <!-- Loading -->
        <div v-if="pending" class="flex items-center justify-center py-12 w-full">
          <p class="font-sans text-[15px] text-muted">Loading orders…</p>
        </div>

        <!-- Rows -->
        <template v-else>
          <div
            v-for="(order, index) in pagedOrders"
            :key="order.id"
            class="flex h-16 items-center px-6 w-full min-w-[800px]"
            :class="{ 'border-t border-stroke': index > 0 }"
          >
            <div class="flex h-16 items-center w-[280px] md:w-[320px]">
              <p class="font-sans font-medium text-sm text-dark">{{ order.name }}</p>
            </div>
            <div class="flex h-16 items-center w-[160px] md:w-[200px]">
              <p class="font-sans text-sm text-secondary">{{ formatDate(order.processedAt) }}</p>
            </div>
            <div class="flex h-16 items-center w-[100px] md:w-[200px]">
              <p class="font-sans text-sm text-secondary">
                {{ order.lineItemCount }} item{{ order.lineItemCount !== 1 ? 's' : '' }}
              </p>
            </div>
            <div class="flex h-16 items-center w-[100px] md:w-[200px]">
              <p class="font-sans font-semibold text-sm text-dark">
                ${{ parseFloat(order.currentTotalPrice.amount).toFixed(2) }}
              </p>
            </div>
            <div class="flex h-16 items-center w-[120px] md:w-[200px]">
              <div class="flex items-center px-3 py-1 rounded-full" :class="statusBg(order.status)">
                <p class="font-sans font-medium text-xs text-secondary">{{ order.status }}</p>
              </div>
            </div>
            <div class="flex flex-1 h-16 items-center justify-end">
              <NuxtLink :to="orderDetailLink(order)" class="font-sans font-medium text-[13px] text-brand whitespace-nowrap">
                View Details →
              </NuxtLink>
            </div>
          </div>

          <!-- Empty state -->
          <div v-if="pagedOrders.length === 0" class="flex items-center justify-center py-12 w-full">
            <p class="font-sans text-[15px] text-muted">No orders found.</p>
          </div>
        </template>
      </div>

      <!-- Pagination -->
      <div v-if="!pending && filteredOrders.length > 0" class="flex h-14 items-center justify-between px-6 w-full">
        <p class="font-sans text-[13px] text-muted">{{ showingText }}</p>
        <div class="flex gap-2 items-center">
          <button
            class="border border-stroke flex items-center px-3 py-2 rounded-md font-sans font-medium text-[13px] text-secondary hover:border-brand transition-colors disabled:opacity-40"
            :disabled="currentPage === 1"
            @click="currentPage--"
          >← Prev</button>
          <button
            v-for="p in totalPages"
            :key="p"
            class="flex items-center px-3 py-2 rounded-md font-sans font-medium text-[13px] transition-colors"
            :class="currentPage === p ? 'bg-brand text-page' : 'border border-stroke text-secondary hover:border-brand'"
            @click="currentPage = p"
          >{{ p }}</button>
          <button
            class="border border-stroke flex items-center px-3 py-2 rounded-md font-sans font-medium text-[13px] text-secondary hover:border-brand transition-colors disabled:opacity-40"
            :disabled="currentPage === totalPages"
            @click="currentPage++"
          >Next →</button>
        </div>
      </div>
    </div>

  </div>
</template>
